import React, { useState, useEffect } from 'react';
import { 
  Search, FileText, Download, Edit, Trash, Upload, 
  Filter, CheckCircle, X, Eye, AlertTriangle, BarChart2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FileManagement = ({ token, accentColor = '#da291c' }) => {
  // State variables
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'uploadDate', direction: 'desc' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [categories, setCategories] = useState([
    'Documents', 'Presentations', 'Spreadsheets', 'Forms', 'Images', 'Others'
  ]);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    description: '',
    category: 'Documents',
    isPublic: true
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    description: '',
    category: '',
    isPublic: false
  });
  
  // Fetch the files when component mounts
  useEffect(() => {
    fetchFiles();
  }, []);
  
  // Fetch files from the server
  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch:443/api/files/admin/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      
      const data = await response.json();
      setFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Could not load files. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('isPublic', uploadForm.isPublic);
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch:443/api/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      const result = await response.json();
      
      // Reset form and close modal
      setUploadForm({
        file: null,
        description: '',
        category: 'Documents',
        isPublic: true
      });
      
      setShowUploadModal(false);
      setSuccessMessage('File uploaded successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      // Refresh file list
      fetchFiles();
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle file deletion
  const handleDelete = async () => {
    if (!fileToDelete) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(`https://lager-app-api.sunrise-avengers.ch:443/api/files/${fileToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
      
      setSuccessMessage('File deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      // Close modal and refresh list
      setShowDeleteModal(false);
      setFileToDelete(null);
      fetchFiles();
      
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file edit/update
  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!fileToEdit) return;
    
    try {
      setLoading(true);
      
      const formData = new URLSearchParams();
      formData.append('description', editForm.description);
      formData.append('category', editForm.category);
      formData.append('isPublic', editForm.isPublic);
      
      const response = await fetch(`https://lager-app-api.sunrise-avengers.ch:443/api/files/${fileToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to update file');
      }
      
      setSuccessMessage('File updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      // Close modal and refresh list
      setShowEditModal(false);
      setFileToEdit(null);
      fetchFiles();
      
    } catch (err) {
      console.error('Error updating file:', err);
      setError('Failed to update file. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file download
  const handleDownload = (file) => {
    window.open(`https://lager-app-api.sunrise-avengers.ch:443/api/files/download/${file.id}`, '_blank');
  };
  
  // Open edit modal with file data
  const openEditModal = (file) => {
    setFileToEdit(file);
    setEditForm({
      description: file.description,
      category: file.category,
      isPublic: file.isPublic
    });
    setShowEditModal(true);
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (file) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };
  
  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle category filter
  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };
  
  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Filter files based on search and category
  const filteredFiles = files.filter(file => {
    const matchesSearch = searchQuery === '' || 
      file.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.description.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesCategory = selectedCategory === 'all' || file.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Sort files based on sort config
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    if (a[sortConfig.key] === null) return 1;
    if (b[sortConfig.key] === null) return -1;
    
    let comparison = 0;
    
    if (sortConfig.key === 'uploadDate') {
      comparison = new Date(a[sortConfig.key]) - new Date(b[sortConfig.key]);
    } else if (sortConfig.key === 'size') {
      comparison = a[sortConfig.key] - b[sortConfig.key];
    } else if (sortConfig.key === 'downloadCount') {
      comparison = a[sortConfig.key] - b[sortConfig.key];
    } else {
      comparison = String(a[sortConfig.key]).toLowerCase().localeCompare(
        String(b[sortConfig.key]).toLowerCase()
      );
    }
    
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get file type icon
  const getFileTypeIcon = (fileType) => {
    if (fileType.includes('pdf')) {
      return <FileText size={24} style={{color: '#ef4444'}} />;
    } else if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('xlsx')) {
      return <BarChart2 size={24} style={{color: '#10b981'}} />;
    } else if (fileType.includes('presentation') || fileType.includes('powerpoint') || fileType.includes('pptx')) {
      return <FileText size={24} style={{color: '#f97316'}} />;
    } else if (fileType.includes('document') || fileType.includes('word') || fileType.includes('docx')) {
      return <FileText size={24} style={{color: '#3b82f6'}} />;
    } else if (fileType.includes('image')) {
      return <Eye size={24} style={{color: '#8b5cf6'}} />;
    } else {
      return <FileText size={24} style={{color: '#6b7280'}} />;
    }
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: '#fff',
      padding: '24px',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '24px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '16px',
    },
    actionBar: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      width: '100%',
    },
    searchContainer: {
      position: 'relative',
      width: '300px',
    },
    searchInput: {
      width: '100%',
      padding: '8px 16px 8px 40px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
    },
    categorySelectContainer: {
      position: 'relative',
      width: '200px',
    },
    categorySelect: {
      width: '100%',
      padding: '8px 16px 8px 40px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      appearance: 'none',
      backgroundColor: '#fff',
    },
    categoryIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '8px',
      color: '#fff',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      border: '1px solid #10b981',
      color: '#065f46',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      border: '1px solid #ef4444',
      color: '#b91c1c',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    alertIcon: {
      marginRight: '8px',
    },
    tableContainer: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
    },
    tableHeaderCell: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      cursor: 'pointer',
      borderBottom: '1px solid #e5e7eb',
    },
    sortIndicator: {
      marginLeft: '4px',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowEven: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '12px 16px',
      fontSize: '14px',
      color: '#1f2937',
      whiteSpace: 'nowrap',
    },
    fileNameCell: {
      display: 'flex',
      alignItems: 'center',
    },
    fileInfo: {
      marginLeft: '16px',
    },
    fileName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1f2937',
      marginBottom: '4px',
    },
    fileDescription: {
      fontSize: '12px',
      color: '#6b7280',
    },
    categoryBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      backgroundColor: '#e0f2fe',
      color: '#0369a1',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '600',
    },
    fileSize: {
      fontSize: '14px',
      color: '#6b7280',
    },
    uploadDate: {
      fontSize: '14px',
      color: '#6b7280',
    },
    downloadCount: {
      fontSize: '14px',
      color: '#6b7280',
    },
    publicStatus: {
      display: 'flex',
      justifyContent: 'center',
    },
    actionsCell: {
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    actionButtons: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    actionButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
    },
    downloadButton: {
      color: '#3b82f6',
    },
    editButton: {
      color: '#eab308',
    },
    deleteButton: {
      color: '#ef4444',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
    },
    modalContent: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      width: '500px',
      maxWidth: '90%',
      maxHeight: '90%',
      overflow: 'auto',
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '20px',
      borderBottom: '1px solid #e5e7eb',
    },
    modalIconContainer: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '9999px',
      marginRight: '16px',
    },
    uploadIconContainer: {
      backgroundColor: '#dbeafe',
      color: '#3b82f6',
    },
    editIconContainer: {
      backgroundColor: '#fef3c7',
      color: '#eab308',
    },
    deleteIconContainer: {
      backgroundColor: '#fee2e2',
      color: '#ef4444',
    },
    modalTitle: {
      flex: 1,
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px',
    },
    modalBody: {
      padding: '20px',
    },
    formGroup: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px',
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      backgroundColor: '#f9fafb',
    },
    textarea: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      backgroundColor: '#f9fafb',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      backgroundColor: '#f9fafb',
      appearance: 'none',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: '#1f2937',
    },
    checkboxInput: {
      marginRight: '8px',
      width: '16px',
      height: '16px',
    },
    progressContainer: {
      marginTop: '16px',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#3b82f6',
      borderRadius: '4px',
      transition: 'width 0.3s ease',
    },
    progressText: {
      fontSize: '12px',
      color: '#6b7280',
      textAlign: 'center',
      marginTop: '4px',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px 20px',
      borderTop: '1px solid #e5e7eb',
      gap: '12px',
    },
    primaryButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      border: 'none',
      color: '#fff',
    },
    cancelButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      color: '#1f2937',
    },
    loadingContainer: {
      padding: '40px',
      textAlign: 'center',
    },
    loadingText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '16px',
      color: '#6b7280',
    },
    emptyContainer: {
      padding: '40px',
      textAlign: 'center',
      color: '#6b7280',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
    },
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' }
    }
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>File Management</h2>
        
        <div style={styles.actionBar}>
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={handleSearch}
              style={styles.searchInput}
            />
            <Search size={18} style={styles.searchIcon} />
          </div>
          
          <div style={styles.categorySelectContainer}>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryFilter(e.target.value)}
              style={styles.categorySelect}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <Filter size={18} style={styles.categoryIcon} />
          </div>
          
          <button
            onClick={() => setShowUploadModal(true)}
            style={{...styles.uploadButton, backgroundColor: accentColor}}
          >
            <Upload size={18} />
            <span>Upload</span>
          </button>
        </div>
      </div>
      
      {/* Success message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={styles.successMessage}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle size={20} style={styles.alertIcon} />
              <span>{successMessage}</span>
            </div>
            <button onClick={() => setSuccessMessage('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={styles.errorMessage}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <AlertTriangle size={20} style={styles.alertIcon} />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Files table */}
      <div style={styles.tableContainer}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th 
                  style={styles.tableHeaderCell}
                  onClick={() => handleSort('fileName')}
                >
                  File Name
                  {sortConfig.key === 'fileName' && (
                    <span style={styles.sortIndicator}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={styles.tableHeaderCell}
                  onClick={() => handleSort('category')}
                >
                  Category
                  {sortConfig.key === 'category' && (
                    <span style={styles.sortIndicator}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={styles.tableHeaderCell}
                  onClick={() => handleSort('size')}
                >
                  Size
                  {sortConfig.key === 'size' && (
                    <span style={styles.sortIndicator}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={styles.tableHeaderCell}
                  onClick={() => handleSort('uploadDate')}
                >
                  Upload Date
                  {sortConfig.key === 'uploadDate' && (
                    <span style={styles.sortIndicator}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={styles.tableHeaderCell}
                  onClick={() => handleSort('downloadCount')}
                >
                  Downloads
                  {sortConfig.key === 'downloadCount' && (
                    <span style={styles.sortIndicator}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={{...styles.tableHeaderCell, textAlign: 'center'}}
                >
                  Public
                </th>
                <th 
                  style={{...styles.tableHeaderCell, textAlign: 'right'}}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={styles.loadingContainer}>
                    <div style={styles.loadingText}>
                      <svg style={styles.spinner} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="#9ca3af" strokeWidth="4" strokeDasharray="32" strokeDashoffset="10" />
                      </svg>
                      <span>Loading files...</span>
                    </div>
                  </td>
                </tr>
              ) : sortedFiles.length === 0 ? (
                <tr>
                  <td colSpan="7" style={styles.emptyContainer}>
                    No files found. Upload your first file.
                  </td>
                </tr>
              ) : (
                sortedFiles.map((file, index) => (
                  <tr key={file.id} style={{...styles.tableRow, ...(index % 2 === 0 ? {} : styles.tableRowEven)}}>
                    <td style={styles.tableCell}>
                      <div style={styles.fileNameCell}>
                        {getFileTypeIcon(file.fileType)}
                        <div style={styles.fileInfo}>
                          <div style={styles.fileName}>{file.fileName}</div>
                          <div style={styles.fileDescription}>{file.description}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.categoryBadge}>
                        {file.category}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.fileSize}>{formatFileSize(file.size)}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.uploadDate}>{formatDate(file.uploadDate)}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.downloadCount}>{file.downloadCount}</span>
                    </td>
                    <td style={{...styles.tableCell, ...styles.publicStatus}}>
                      {file.isPublic ? (
                        <CheckCircle size={20} style={{color: '#10b981'}} />
                      ) : (
                        <X size={20} style={{color: '#ef4444'}} />
                      )}
                    </td>
                    <td style={{...styles.tableCell, ...styles.actionsCell}}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => handleDownload(file)}
                          style={{...styles.actionButton, ...styles.downloadButton}}
                          title="Download"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(file)}
                          style={{...styles.actionButton, ...styles.editButton}}
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(file)}
                          style={{...styles.actionButton, ...styles.deleteButton}}
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div style={styles.modalOverlay}>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={styles.modalContent}
          >
            <div style={styles.modalHeader}>
              <div style={{...styles.modalIconContainer, ...styles.uploadIconContainer}}>
                <Upload size={24} />
              </div>
              <div>
                <h3 style={styles.modalTitle}>Upload New File</h3>
              </div>
            </div>
            
            <div style={styles.modalBody}>
              <form onSubmit={handleUpload}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="file">File</label>
                  <input
                    type="file"
                    id="file"
                    onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                    style={styles.input}
                    required
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                    style={styles.textarea}
                    required
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="category">Category</label>
                  <select
                    id="category"
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                    style={styles.select}
                    required
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={uploadForm.isPublic}
                      onChange={(e) => setUploadForm({...uploadForm, isPublic: e.target.checked})}
                      style={styles.checkboxInput}
                    />
                    <span>Make file public</span>
                  </label>
                </div>
                
                {uploading && (
                  <div style={styles.progressContainer}>
                    <div style={styles.progressBar}>
                      <div 
                        style={{
                          ...styles.progressFill,
                          width: `${uploadProgress}%`
                        }}
                      ></div>
                    </div>
                    <p style={styles.progressText}>{uploadProgress}% Uploaded</p>
                  </div>
                )}
              </form>
            </div>
            
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.cancelButton}
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{...styles.primaryButton, backgroundColor: accentColor}}
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && fileToEdit && (
        <div style={styles.modalOverlay}>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={styles.modalContent}
          >
            <div style={styles.modalHeader}>
              <div style={{...styles.modalIconContainer, ...styles.editIconContainer}}>
                <Edit size={24} />
              </div>
              <div>
                <h3 style={styles.modalTitle}>Edit File</h3>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>{fileToEdit.fileName}</p>
              </div>
            </div>
            
            <div style={styles.modalBody}>
              <form onSubmit={handleEdit}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="edit-description">Description</label>
                  <textarea
                    id="edit-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    style={styles.textarea}
                    required
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="edit-category">Category</label>
                  <select
                    id="edit-category"
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                    style={styles.select}
                    required
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={editForm.isPublic}
                      onChange={(e) => setEditForm({...editForm, isPublic: e.target.checked})}
                      style={styles.checkboxInput}
                    />
                    <span>Make file public</span>
                  </label>
                </div>
              </form>
            </div>
            
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.cancelButton}
                onClick={() => setShowEditModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{...styles.primaryButton, backgroundColor: '#eab308'}}
                onClick={handleEdit}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && fileToDelete && (
        <div style={styles.modalOverlay}>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={styles.modalContent}
          >
            <div style={styles.modalHeader}>
              <div style={{...styles.modalIconContainer, ...styles.deleteIconContainer}}>
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 style={styles.modalTitle}>Delete File</h3>
              </div>
            </div>
            
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                Are you sure you want to delete the following file? This action cannot be undone.
              </p>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                {fileToDelete.fileName}
              </p>
            </div>
            
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{...styles.primaryButton, backgroundColor: '#ef4444'}}
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FileManagement;